<template>
	<v-row class="full-height">
		<v-col cols="12" class="flex-none grid-header">
			<div class="rep-header-actions">
				<v-btn depressed rounded color="success" @click="newRep()" :disabled="activeEdit !== null">
					New Rep
				</v-btn>
			</div>
		</v-col>

		<v-col cols="12" class="flex-auto">
			<div class="rep-table">
				<div v-if="initData" class="loading-overlay">
					<div class="loading-dot">&nbsp;</div>
					<div class="loading-dot">&nbsp;</div>
					<div class="loading-dot">&nbsp;</div>
					<div class="loading-dot">&nbsp;</div>
					<div class="loading-dot">&nbsp;</div>
				</div>
				<div class="flex-table" role="table-head">
					<div class="flex-table-row" role="row-group">
						<div class="flex-table-cell" role="column-header">&nbsp;</div>
						<div class="flex-table-cell" role="column-header">Rep #</div>
						<div class="flex-table-cell" role="column-header">Name</div>
						<div class="flex-table-cell" role="column-header">Email</div>
						<div class="flex-table-cell" role="column-header">Phone Number</div>
						<div class="flex-table-cell" role="column-header">Inactive Date</div>
						<div class="flex-table-cell" role="column-header">&nbsp;</div>
						<div class="flex-table-cell" role="column-header">&nbsp;</div>
					</div>
				</div>
				<div
					class="flex-table"
					role="table-body"
					@drop="onDrop()"
					@dragover.prevent="startSort ? sortItems($event) : null"
				>
					<div
						v-for="(rep, i) in repData"
						:key="i"
						class="flex-table-row"
						role="row-group"
						:style="`order: ${rep.sequence}`"
						:draggable="startSort"
					>
						<template v-if="rep == activeEdit">
							<div class="flex-table-cell" role="cell">
								<v-icon
									style="font-size: 2rem"
									v-text="'mdi-arrow-split-horizontal'"
									@mousedown="triggerSorting($event, rep)"
								></v-icon>
							</div>
							<div v-if="rep.salesRepId !== null" class="flex-table-cell" role="cell">
								{{ rep.salesRepNumber }}
							</div>
							<div v-else class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="rep.salesRepNumber"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="rep.name"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="rep.email"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<div class="form-input">
									<v-text-field
										outlined
										dense
										hide-details="auto"
										v-model="rep.phoneNumber"
									></v-text-field>
								</div>
							</div>
							<div class="flex-table-cell" role="cell">
								<v-menu
									v-model="showInactiveDate"
									:close-on-content-click="false"
									transition="scale-transition"
									offset-y
									min-width="290px"
								>
									<template v-slot:activator="{ on, attrs }">
										<v-text-field
											v-model="formattedInactiveDate"
											append-icon="mdi-calendar"
											@click:append="showInactiveDate = true"
											outlined
											dense
											v-bind="attrs"
											v-on="on"
											@blur="rep.inactiveDate = parseDate(formattedInactiveDate)"
											hide-details="auto"
										></v-text-field>
									</template>
									<v-date-picker
										v-model="rep.inactiveDate"
										@input="showInactiveDate = false"
									></v-date-picker>
								</v-menu>
							</div>
							<div class="flex-table-cell" role="cell">
								<v-btn
									icon
									color="primary"
									@click="rep.salesRepId == null ? removeRep() : (activeEdit = null)"
									><v-icon style="font-size: 2rem">mdi-close</v-icon>
								</v-btn>
							</div>
							<div class="flex-table-cell" role="cell">
								<v-btn icon color="primary" @click="saveRep(rep)"
									><v-icon style="font-size: 2rem">mdi-check</v-icon>
								</v-btn>
							</div>
						</template>
						<template v-else>
							<div class="flex-table-cell" role="cell">
								<v-icon
									v-if="activeEdit"
									style="font-size: 2rem"
									v-text="'mdi-arrow-split-horizontal'"
									@mousedown="triggerSorting($event, rep)"
								></v-icon>
							</div>
							<div class="flex-table-cell" role="cell">{{ rep.salesRepNumber }}</div>
							<div class="flex-table-cell" role="cell">
								{{ rep.name }}
							</div>
							<div class="flex-table-cell" role="cell">
								{{ rep.email }}
							</div>
							<div class="flex-table-cell" role="cell">
								{{ rep.phoneNumber }}
							</div>
							<div class="flex-table-cell" role="cell">
								{{ formatDate(rep.inactiveDate) }}
							</div>
							<div class="flex-table-cell" role="cell">&nbsp;</div>
							<div class="flex-table-cell" role="cell">
								<v-btn icon color="primary" @click="editRep(rep)"
									><v-icon style="font-size: 2rem">mdi-pencil</v-icon>
								</v-btn>
							</div>
						</template>
					</div>
				</div>
			</div>
			<div class="table-controls">
				<v-btn
					depressed
					small
					:disabled="!hasPreviousPage"
					@click="
						pageIndexOffset -= 15;
						getSalesReps();
					"
				>
					Previous
				</v-btn>
				<v-btn
					depressed
					small
					:disabled="!hasNextPage"
					@click="
						pageIndexOffset += 15;
						getSalesReps();
					"
				>
					Next Page
				</v-btn>
			</div>
		</v-col>
	</v-row>
</template>

<script>
import gql from "graphql-tag";
import { getApiUrl } from "@/helpers";
export default {
	name: "Rep",
	data() {
		return {
			initData: false,
			repData: [],
			activeEdit: null,
			showInactiveDate: false,
			formattedInactiveDate: this.formatDate(
				new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().substr(0, 10)
			),
			pageIndexOffset: 0,
			hasNextPage: false,
			hasPreviousPage: false,
			startSort: false,
			dragItemElement: null,
			dragItem: null,
		};
	},
	methods: {
		formatDate(date) {
			if (!date) return "";

			date = date.substring(0, 10);
			const [year, month, day] = date.split("-");
			return `${month}/${day}/${year}`;
		},

		parseDate(date) {
			if (!date) return null;
			const [month, day, year] = date.split("/");
			return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
		},

		newRep() {
			const newRep = {
				salesRepId: null,
				salesRepNumber: "",
				name: "",
				inactiveDate: null,
				sequence: 0,
				email: "",
				phoneNumber: "",
			};

			this.repData.unshift(newRep);

			//set the new rep as the active edit
			this.activeEdit = newRep;
		},

		editRep(rep) {
			let repData = rep;
			//strip time stamp on inactiveDate field
			if (repData.inactiveDate && repData.inactiveDate.includes("T")) {
				repData.inactiveDate = repData.inactiveDate.split("T").shift();
			}

			this.activeEdit = repData;
		},

		removeRep() {
			this.repData.splice(0, 1);
			this.activeEdit = null;
		},

		async saveRep(rep) {
			try {
				let res;
				if (rep.salesRepId == null) {
					res = await this.$http.post(getApiUrl("/api/customers/sales-reps"), rep);
				} else {
					res = await this.$http.put(getApiUrl("/api/customers/sales-reps"), rep);
				}

				//assign the reps id
				if (res.data.salesRepId) {
					this.activeEdit.salesRepId = res.data.salesRepId;
				}

				//update sequence list for newest items
				await this.updateSequenceList(res.data.salesRepId);

				this.activeEdit = null;
				this.getSalesReps();
			} catch (error) {
				console.log(error);
			}
		},

		async getSalesReps() {
			try {
				const res = await this.$apollo.query({
					query: gql`
						query GetSalesReps($pageOffset: Int!) {
							salesReps(skip: $pageOffset, take: 15, order: { name: ASC }) {
								items {
									salesRepId
									salesRepNumber
									name
									inactiveDate
									sequence
									email
									phoneNumber
								}
								pageInfo {
									hasNextPage
									hasPreviousPage
								}
							}
						}
					`,
					variables: {
						pageOffset: this.pageIndexOffset,
					},
					fetchPolicy: "no-cache",
				});

				this.hasNextPage = res.data.salesReps.pageInfo.hasNextPage;
				this.hasPreviousPage = res.data.salesReps.pageInfo.hasPreviousPage;
				this.repData = res.data.salesReps.items;

				if (this.initData) {
					this.initData = false;
				}
			} catch (error) {
				console.log(error);
			}
		},

		async updateSequenceList(id) {
			try {
				//create list of sequeneces and ids
				let sequenceList = this.repData.map((obj) => {
					let sequenceObj = {
						//if no id is defined then use the passed in id (that means the user created a new entry and this needs to be added to the sequence list)
						entityId: obj.salesRepId ? obj.salesRepId : id,
						//if user is using paging then we need to add the index offset to the sequence count
						sequence: this.pageIndexOffset !== 0 ? obj.sequence + this.pageIndexOffset : obj.sequence,
					};
					return sequenceObj;
				});

				const res = await this.$http.put(
					getApiUrl("/api/customers/sales-reps/sequence"),
					{ sequenced: sequenceList }
				);
				console.log(res);

				return;
			} catch (error) {
				console.log(error);
			}
		},

		//drag and drop sorting functions
		onDrop() {
			//if sorting is active reset dragging states after dropping the dragged item
			if (this.dragItem) {
				this.startSort = false;
				this.dragItemElement.classList.remove("dragging");
				this.dragItemElement = null;
				this.dragItem = null;
			}
		},

		triggerSorting(event, item) {
			this.startSort = true;
			//set dragging element in data and add dragging class (this is removed on drag drop event)
			this.dragItemElement = event.target.closest(".flex-table-row");
			this.dragItemElement.classList.add("dragging");

			//set drag item in data
			this.dragItem = item;
		},

		renderItemOrder(item) {
			//find the matching item in sequence list
			const matchingSortItem = this.sequenceList.find((obj) => obj.salesRepId == item.salesRepId);

			return matchingSortItem ? matchingSortItem.sequence : 0;
		},

		setItemOrder(closestItem) {
			let sortedList = [];

			//get all items except the current drag item and sort them in order
			sortedList = this.repData
				.filter((obj) => obj !== this.dragItem)
				.sort((a, b) => a.sequence - b.sequence);

			if (closestItem) {
				//find the closestItem in data by DOM style order property
				let closestSortItemIndex = sortedList.findIndex((obj) => obj.sequence == closestItem.style.order);

				//assign dragItem above the passed in closestItem
				sortedList.splice(closestSortItemIndex, 0, this.dragItem);
			} else {
				//assign the selected item to the end of its level
				sortedList.push(this.dragItem);
			}

			//set the new sequence order via index + 1
			sortedList = sortedList.map((obj, i) => {
				return { ...obj, sequence: i + 1 };
			});

			//update rep list with new sequences from the sortedList
			sortedList.forEach((item) => {
				let foundIndex = this.repData.findIndex((obj) => obj.salesRepId == item.salesRepId);
				this.repData[foundIndex].sequence = item.sequence;
			});
		},

		sortItems(event) {
			//get all item elements that are not the dragged item
			const nonActiveItemElements = [
				...event.target.closest(".flex-table").querySelectorAll(".flex-table-row:not(.dragging)"),
			];

			//find the closest item that were above in the DOM
			let closestItem = nonActiveItemElements.reduce(
				(closest, child) => {
					const box = child.getBoundingClientRect();
					const offset = event.clientY - box.top - box.height / 2;
					if (offset < 0 && offset > closest.offset) {
						return { offset: offset, element: child };
					} else {
						return closest;
					}
				},
				{ offset: Number.NEGATIVE_INFINITY }
			).element;

			//change the item order in data by element names
			if (closestItem == null) {
				this.setItemOrder();
			} else {
				this.setItemOrder(closestItem);
			}
		},
	},
	watch: {
		"activeEdit.inactiveDate"(value) {
			this.formattedInactiveDate = this.formatDate(value);
		},
		"activeEdit.salesRepNumber"(value) {
			if (this.activeEdit) {
				this.activeEdit.loginName = value;
			}
		},
	},
	mounted() {
		this.initData = true;
		//get sales reps list on load
		this.getSalesReps();
	},
};
</script>

<style lang="scss" scoped>
.rep {
	&-header {
		&-actions {
			grid-column: 3;
			margin-left: auto;

			.v-btn {
				min-width: 13rem;
				text-transform: initial;

				&:first-child {
					background-color: $color-white;
					border-color: $color-gray;
				}
			}
		}
	}

	&-table {
		position: relative;
		height: 0;
		overflow-y: auto;
		flex-grow: 1;
		background-color: $color-white;
		border-radius: 3px;

		.form-input {
			width: 100%;
			margin: 0;
		}

		.flex-table {
			border: none;

			&[role="table-head"] {
				position: sticky;
				top: 0;
				z-index: 1;
			}

			&[role="table-body"] {
				.flex-table-row {
					border-top: none;
					border-bottom: solid 1px $color-gray;
				}
			}
		}

		.flex-table-cell {
			&:nth-child(1) {
				width: 3%;
				justify-content: center;
			}
			&:nth-child(2) {
				width: 6%;
			}
			&:nth-child(3) {
				width: 30%;
			}
			&:nth-child(4) {
				width: 19%;
			}
			&:nth-child(5) {
				width: 19%;
			}
			&:nth-child(6) {
				width: 15%;
			}
			&:nth-child(7) {
				width: 4%;
			}
			&:nth-child(8) {
				width: 4%;
			}
		}
	}
}
</style>
